import "../scss/pages/educationInfo.scss";

/**
 * Модуль отрисовки страницы "Сведения об образовательной организации".
 * Загружает данные через API /api/education-info/ и динамически отрисовывает контент.
 */

const API_URL = "/api/education-info/";

async function fetchEducationData() {
  const response = await fetch(API_URL);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/**
 * Создаёт заголовок секции — безопасная альтернатива innerHTML.
 */
function createSectionTitle(text) {
  const h2 = document.createElement("h2");
  h2.className = "education-section__title";
  h2.textContent = text;
  return h2;
}

/**
 * Создаёт сообщение о пустых данных — безопасная альтернатива innerHTML.
 */
function createEmptyMessage() {
  const p = document.createElement("p");
  p.className = "education-section__empty";
  p.textContent = "Данные отсутствуют.";
  return p;
}

/**
 * Создаёт элемент навигации
 */
function buildNavList(sections) {
  const list = document.getElementById("education-nav-list");
  if (!list) return;
  list.replaceChildren();

  sections.forEach((section) => {
    const anchor = section["Адрес (якорь)"] || "";
    const title = section["Название раздела"] || "";
    const li = document.createElement("li");
    li.className = "education-nav__item";

    const link = document.createElement("a");
    link.href = anchor || "#";
    link.className = "education-nav__link";
    link.textContent = title;
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const targetId = anchor.replace("#", "");
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      // Подсветка активного пункта
      document.querySelectorAll(".education-nav__link").forEach((l) =>
        l.classList.remove("education-nav__link--active")
      );
      link.classList.add("education-nav__link--active");
    });

    li.appendChild(link);
    list.appendChild(li);
  });
}

/**
 * Отрисовывает секцию "Основные сведения"
 */
function renderBasicInfo(data, container) {
  const section = document.createElement("section");
  section.id = "section-1";
  section.className = "education-section";
  section.appendChild(createSectionTitle("Основные сведения"));

  const table = document.createElement("table");
  table.className = "education-table";

  Object.entries(data).forEach(([key, value]) => {
    const tr = document.createElement("tr");
    const keyTd = document.createElement("td");
    keyTd.className = "education-table__key";
    keyTd.textContent = key;
    const valueTd = document.createElement("td");
    valueTd.className = "education-table__value";
    appendValueContent(valueTd, value);
    tr.appendChild(keyTd);
    tr.appendChild(valueTd);
    table.appendChild(tr);
  });

  section.appendChild(table);
  container.appendChild(section);
}

/**
 * Отрисовывает секцию "Структура и органы управления"
 */
function renderStructure(data, container) {
  const section = document.createElement("section");
  section.id = "section-2";
  section.className = "education-section";
  section.appendChild(createSectionTitle("Структура и органы управления"));

  if (data.length === 0) {
    section.appendChild(createEmptyMessage());
    container.appendChild(section);
    return;
  }

  const table = buildTableFromRows(data);
  section.appendChild(table);
  container.appendChild(section);
}

/**
 * Отрисовывает секцию "Документы"
 */
function renderDocuments(data, container) {
  const section = document.createElement("section");
  section.id = "section-3";
  section.className = "education-section";
  section.appendChild(createSectionTitle("Документы"));

  if (data.length === 0) {
    section.appendChild(createEmptyMessage());
    container.appendChild(section);
    return;
  }

  const table = buildTableFromRows(data);
  section.appendChild(table);
  container.appendChild(section);
}

/**
 * Отрисовывает секцию "Образование"
 */
function renderEducation(data, container) {
  const section = document.createElement("section");
  section.id = "section-4";
  section.className = "education-section";
  section.appendChild(createSectionTitle("Образование"));

  if (data.length === 0) {
    section.appendChild(createEmptyMessage());
    container.appendChild(section);
    return;
  }

  const table = buildTableFromRows(data);
  table.classList.add("education-table--scrollable");
  section.appendChild(table);
  container.appendChild(section);
}

/**
 * Отрисовывает секцию "Руководство"
 */
function renderManagement(data, container) {
  const section = document.createElement("section");
  section.id = "section-5";
  section.className = "education-section";
  section.appendChild(createSectionTitle("Руководство"));

  if (data.length === 0) {
    section.appendChild(createEmptyMessage());
    container.appendChild(section);
    return;
  }

  const table = buildTableFromRows(data);
  section.appendChild(table);
  container.appendChild(section);
}

/**
 * Отрисовывает секцию "Педагогический состав"
 */
function renderTeachingStaff(data, container) {
  const section = document.createElement("section");
  section.id = "section-6";
  section.className = "education-section";
  section.appendChild(createSectionTitle("Педагогический состав"));

  if (data.length === 0) {
    section.appendChild(createEmptyMessage());
    container.appendChild(section);
    return;
  }

  const table = buildTableFromRows(data);
  table.classList.add("education-table--scrollable");
  section.appendChild(table);
  container.appendChild(section);
}

/**
 * Отрисовывает секцию "Материально-техническое обеспечение"
 */
function renderMaterialResources(data, container) {
  const section = document.createElement("section");
  section.id = "section-7";
  section.className = "education-section";
  section.appendChild(createSectionTitle("Материально-техническое обеспечение"));

  const table = document.createElement("table");
  table.className = "education-table";

  Object.entries(data).forEach(([key, value]) => {
    const tr = document.createElement("tr");
    const keyTd = document.createElement("td");
    keyTd.className = "education-table__key";
    keyTd.textContent = key;
    const valueTd = document.createElement("td");
    valueTd.className = "education-table__value";
    appendValueContent(valueTd, value);
    tr.appendChild(keyTd);
    tr.appendChild(valueTd);
    table.appendChild(tr);
  });

  section.appendChild(table);
  container.appendChild(section);
}

/**
 * Строит таблицу из массива строк (списка словарей)
 */
function buildTableFromRows(rows) {
  if (rows.length === 0) return document.createElement("table");

  const table = document.createElement("table");
  table.className = "education-table";

  // Заголовок таблицы
  const thead = document.createElement("thead");
  const headerTr = document.createElement("tr");
  Object.keys(rows[0]).forEach((key) => {
    const th = document.createElement("th");
    th.textContent = key;
    headerTr.appendChild(th);
  });
  thead.appendChild(headerTr);
  table.appendChild(thead);

  // Тело таблицы
  const tbody = document.createElement("tbody");
  rows.forEach((row) => {
    const tr = document.createElement("tr");
    Object.values(row).forEach((value) => {
      const td = document.createElement("td");
      appendValueContent(td, value);
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);

  return table;
}

/**
 * Определяет, является ли значение веб-ссылкой.
 */
const LINK_RE = /(https?:\/\/[^\s;]+|mailto:[^\s;]+|\/media\/[^\s;]+)/g;

/**
 * Добавляет в ячейку содержимое, превращая ссылки в кликабельные <a>.
 */
function appendValueContent(cell, value) {
  const text = String(value == null ? "" : value);
  let lastIndex = 0;
  let match;
  const nodes = [];

  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(document.createTextNode(text.slice(lastIndex, match.index)));
    }
    const link = document.createElement("a");
    link.href = match[0];
    link.textContent = match[0];
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    nodes.push(link);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(document.createTextNode(text.slice(lastIndex)));
  }
  if (nodes.length === 0) {
    nodes.push(document.createTextNode(text));
  }

  nodes.forEach((node) => cell.appendChild(node));
}

/**
 * Показывает сообщение об ошибке. Использует DOM-методы вместо innerHTML.
 */
function showError(container, message) {
  const wrapper = document.createElement("div");
  wrapper.className = "education-info__error";

  const textPara = document.createElement("p");
  textPara.textContent = message;
  wrapper.appendChild(textPara);

  const retryBtn = document.createElement("button");
  retryBtn.className = "btn btn--primary";
  retryBtn.textContent = "Попробовать снова";
  retryBtn.addEventListener("click", () => {
    location.reload();
  });
  wrapper.appendChild(retryBtn);

  container.replaceChildren(wrapper);
}

/**
 * Главная функция инициализации
 */
async function initEducationPage() {
  const contentEl = document.getElementById("education-content");
  if (!contentEl) return;

  try {
    const data = await fetchEducationData();

    // Строим навигацию
    if (data.sections) {
      buildNavList(data.sections);
    }

    // Очищаем контент и строим секции
    contentEl.replaceChildren();

    if (data.basic_info) {
      renderBasicInfo(data.basic_info, contentEl);
    }
    if (data.structure) {
      renderStructure(data.structure, contentEl);
    }
    if (data.documents) {
      renderDocuments(data.documents, contentEl);
    }
    if (data.education) {
      renderEducation(data.education, contentEl);
    }
    if (data.management) {
      renderManagement(data.management, contentEl);
    }
    if (data.teaching_staff) {
      renderTeachingStaff(data.teaching_staff, contentEl);
    }
    if (data.material_resources) {
      renderMaterialResources(data.material_resources, contentEl);
    }
  } catch (error) {
    console.error("Ошибка загрузки данных:", error);
    showError(contentEl, "Не удалось загрузить данные. Пожалуйста, попробуйте позже.");
  }
}

// Запуск при загрузке DOM
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initEducationPage);
} else {
  initEducationPage();
}